export interface Waypoint {
  id: string;
  x: number;
  y: number;
  rad: number;
  rot: number;
}

export const PATH_MARKER_TYPES = {
  SEGMENT_PERCENT: 'segment_percent',
  DISTANCE_FROM_START: 'distance_from_start',
  DISTANCE_FROM_END: 'distance_from_end',
} as const;

export type PathMarkerType = (typeof PATH_MARKER_TYPES)[keyof typeof PATH_MARKER_TYPES];

/**
 * Serialisable shape for an action assigned to a path marker.
 * Structurally identical to ProgramStartActionConfig (hardwareParser) to avoid
 * a cross-package dependency.
 */
export type MarkerActionConfig = {
  /** Framework action label, e.g. "motor.stop_action". Empty string means "no action". */
  selection: string;
  argValues: Record<string, string>;
  rawExpression?: string;
};

export interface PathMarker {
  id: string;
  /** Human-readable name, e.g. marker_A */
  name: string;
  /** Index of the segment from wp[i] to wp[i+1] where this marker lives. */
  segmentIndex: number;
  type: PathMarkerType;
  /** Percentage (0–100) for segment_percent, or meters for distance types. Corner radius is also in meters. */
  value: number;
  /** Action to trigger when the robot reaches this marker. */
  action?: MarkerActionConfig;
}

export const PATH_PREVIEW_MODES = {
  SWERVE: 'swerve',
  SKID: 'skid',
} as const;

export type PathPreviewMode = (typeof PATH_PREVIEW_MODES)[keyof typeof PATH_PREVIEW_MODES];

export const DEFAULT_PATH_PREVIEW_MODE: PathPreviewMode = PATH_PREVIEW_MODES.SWERVE;

export interface Point {
  x: number;
  y: number;
}

export interface Path {
  name: string;
  id: string;
  isVisible: boolean;
  wp: Waypoint[];
  /** Speed (m/s) from waypoint[i] to waypoint[i+1]. Length = wp.length - 1. */
  spd: number[];
  /** Markers placed on path segments. */
  mrk?: PathMarker[];
}

export interface ContextMenu {
  mouseX: number;
  mouseY: number;
  coords: Point;
  clickedWaypoint: Waypoint | null;
  /** Index of segment from waypoint[i] to waypoint[i+1] when opening menu on a path stroke. */
  clickedSegmentIndex: number | null;
  insertIndex: number | null;
  /** Marker that was right-clicked, if any. */
  clickedMarker: PathMarker | null;
}
